/**
 * Events Discovery Page
 * Airbnb-style split layout: filterable event list + map view.
 */

import { listPublicEvents, PublicEvent } from './api/firebase';
import { renderBreadcrumbs } from './components/Breadcrumbs';
import { renderDownloadCTA, renderFooter } from './components/DownloadCTA';
import { setPageMeta, setJsonLd, setCanonical } from './utils/seo';

// Pagination state
let paginationState = {
  events: [] as PublicEvent[],
  lastCursor: null as string | null,
  hasMore: true,
  isLoading: false,
  statusFilter: '' as string,
  searchQuery: '',
};

// Map instance
let mapInstance: any = null;
let mapMarkers: any[] = [];

// Geocode cache (locality → [lat, lng])
const geocodeCache: Record<string, [number, number] | null> = {};

async function geocodeLocality(locality: string): Promise<[number, number] | null> {
  if (locality in geocodeCache) return geocodeCache[locality];
  try {
    const resp = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locality)}&format=json&limit=1`,
      { headers: { 'User-Agent': 'EcstaticOracle/1.0' } }
    );
    const results = await resp.json();
    if (results.length > 0) {
      const coords: [number, number] = [parseFloat(results[0].lat), parseFloat(results[0].lon)];
      geocodeCache[locality] = coords;
      return coords;
    }
  } catch (e) {
    console.warn('Geocode failed for', locality, e);
  }
  geocodeCache[locality] = null;
  return null;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function formatScheduledTime(isoString: string): string {
  const dt = new Date(isoString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateDay = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  const diffDays = Math.floor((dateDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  let dateStr: string;
  if (diffDays === 0) dateStr = 'Today';
  else if (diffDays === 1) dateStr = 'Tomorrow';
  else if (diffDays === -1) dateStr = 'Yesterday';
  else {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    dateStr = `${months[dt.getMonth()]} ${dt.getDate()}`;
  }

  const hour = dt.getHours().toString().padStart(2, '0');
  const minute = dt.getMinutes().toString().padStart(2, '0');
  return `${dateStr} at ${hour}:${minute}`;
}

function getStatusBadge(status: string): string {
  switch (status) {
    case 'lobby':
      return '<span class="event-status-pill lobby">&#x1F551; Upcoming</span>';
    case 'active':
      return '<span class="event-status-pill active"><span class="pulse-dot-small"></span> Live</span>';
    case 'ended':
      return '<span class="event-status-pill ended">&#x2705; Ended</span>';
    default:
      return `<span class="event-status-pill">${escapeHtml(status)}</span>`;
  }
}

function renderEventCard(event: PublicEvent): string {
  const locationText = event.location?.locality || '';
  const timeText = event.scheduledStartTime
    ? formatScheduledTime(event.scheduledStartTime)
    : '';
  const maxDisplay = event.maxDevices >= 9999 ? '&infin;' : String(event.maxDevices);

  return `
    <a href="/event/${escapeHtml(event.joinCode)}" class="event-card"
       data-event-id="${escapeHtml(event.eventId)}"
       data-lat="${event.location?.latitude || ''}"
       data-lng="${event.location?.longitude || ''}">
      <div class="event-card-top">
        ${getStatusBadge(event.status)}
      </div>
      <h3 class="event-card-title">${escapeHtml(event.title)}</h3>
      <div class="event-card-meta">
        ${locationText ? `<span class="event-card-location">&#x1F4CD; ${escapeHtml(locationText)}</span>` : ''}
        ${timeText ? `<span class="event-card-time">&#x1F552; ${escapeHtml(timeText)}</span>` : ''}
      </div>
      <div class="event-card-bottom">
        <span class="event-card-organizer">by ${escapeHtml(event.organizerName)}</span>
        <span class="event-card-participants">${event.participantCount}/${maxDisplay} joined</span>
      </div>
    </a>
  `;
}

async function loadEvents(status: string, reset: boolean): Promise<void> {
  const listEl = document.getElementById('events-list');
  const countEl = document.getElementById('events-count');
  if (!listEl) return;

  if (reset) {
    paginationState = {
      events: [],
      lastCursor: null,
      hasMore: true,
      isLoading: false,
      statusFilter: status,
      searchQuery: paginationState.searchQuery,
    };
    listEl.innerHTML = `
      <div class="loading-state">
        <div class="loading-spinner"></div>
        <span>Loading events...</span>
      </div>
    `;
  }

  if (paginationState.isLoading) return;
  paginationState.isLoading = true;

  try {
    const result = await listPublicEvents(
      20,
      paginationState.lastCursor || undefined,
      status || undefined
    );

    paginationState.events = [...paginationState.events, ...result.events];
    paginationState.lastCursor = result.lastCursor;
    paginationState.hasMore = result.hasMore;
    paginationState.isLoading = false;

    renderEventsList();
    await updateMapMarkers();

    if (countEl) {
      countEl.textContent = `${paginationState.events.length} event${paginationState.events.length !== 1 ? 's' : ''}`;
    }
  } catch (error) {
    console.error('Failed to load events:', error);
    paginationState.isLoading = false;
    listEl.innerHTML = `
      <div class="error-state">
        <span class="error-icon">&#x26A0;&#xFE0F;</span>
        <p>Failed to load events</p>
        <button onclick="window.location.reload()" class="retry-btn">Retry</button>
      </div>
    `;
  }
}

function renderEventsList(): void {
  const listEl = document.getElementById('events-list');
  if (!listEl) return;

  let events = paginationState.events;

  // Client-side search filter
  if (paginationState.searchQuery) {
    const q = paginationState.searchQuery.toLowerCase();
    events = events.filter((e) =>
      e.title.toLowerCase().includes(q) ||
      e.organizerName.toLowerCase().includes(q) ||
      e.location?.locality?.toLowerCase().includes(q) ||
      e.location?.country?.toLowerCase().includes(q)
    );
  }

  if (events.length === 0) {
    listEl.innerHTML = `
      <div class="empty-state">
        <span class="empty-icon">&#x1F4C5;</span>
        <p>No events found</p>
        <p class="empty-hint">${paginationState.searchQuery ? 'Try a different search' : 'Events will appear here when created in the app'}</p>
      </div>
    `;
    return;
  }

  listEl.innerHTML = events.map((e) => renderEventCard(e)).join('');

  if (paginationState.hasMore && !paginationState.searchQuery) {
    listEl.innerHTML += `
      <div class="load-more-trigger">
        <div class="loading-spinner small"></div>
        <span>Loading more...</span>
      </div>
    `;
  }

  // Card hover → highlight map marker
  listEl.querySelectorAll('.event-card').forEach((card) => {
    card.addEventListener('mouseenter', () => {
      const id = (card as HTMLElement).dataset.eventId;
      highlightMarker(id || '');
    });
    card.addEventListener('mouseleave', () => {
      unhighlightMarkers();
    });
  });
}

async function initEventsMap(): Promise<void> {
  const mapEl = document.getElementById('events-map');
  if (!mapEl || typeof (window as any).L === 'undefined') return;

  const L = (window as any).L;

  mapInstance = L.map('events-map', {
    zoomControl: true,
    attributionControl: true,
  }).setView([52.0, 5.3], 7); // Default: Netherlands

  L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
    maxZoom: 19,
  }).addTo(mapInstance);

  await updateMapMarkers();
}

async function updateMapMarkers(): Promise<void> {
  if (!mapInstance) return;
  const L = (window as any).L;

  // Clear existing markers
  mapMarkers.forEach((m) => m.remove());
  mapMarkers = [];

  // Resolve coordinates: use existing lat/lng or geocode locality
  const eventsWithCoords: { event: PublicEvent; lat: number; lng: number }[] = [];

  const geocodePromises = paginationState.events
    .filter((e) => e.location?.locality || (e.location?.latitude && e.location?.longitude))
    .map(async (event) => {
      if (event.location?.latitude && event.location?.longitude) {
        eventsWithCoords.push({ event, lat: event.location.latitude, lng: event.location.longitude });
      } else if (event.location?.locality) {
        const coords = await geocodeLocality(event.location.locality);
        if (coords) {
          eventsWithCoords.push({ event, lat: coords[0], lng: coords[1] });
        }
      }
    });

  await Promise.all(geocodePromises);

  if (eventsWithCoords.length === 0) return;

  const markerIcon = L.divIcon({
    className: 'event-marker',
    html: '<div class="event-marker-dot"></div>',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });

  const activeMarkerIcon = L.divIcon({
    className: 'event-marker active',
    html: '<div class="event-marker-dot active"></div>',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

  eventsWithCoords.forEach(({ event, lat, lng }) => {
    const icon = event.status === 'active' ? activeMarkerIcon : markerIcon;
    const marker = L.marker([lat, lng], { icon });

    const popup = `
      <div class="map-popup">
        ${getStatusBadge(event.status)}
        <h3 class="popup-title">${escapeHtml(event.title)}</h3>
        <p class="popup-location">${escapeHtml(event.location?.locality || '')}</p>
        <p class="popup-author">by ${escapeHtml(event.organizerName)}</p>
        <a href="/event/${escapeHtml(event.joinCode)}" class="popup-link">View Event &rarr;</a>
      </div>
    `;

    marker.bindPopup(popup, { maxWidth: 250, className: 'custom-popup' });
    marker.eventId = event.eventId;
    marker.addTo(mapInstance);
    mapMarkers.push(marker);
  });

  // Fit bounds
  if (mapMarkers.length > 1) {
    const group = L.featureGroup(mapMarkers);
    mapInstance.fitBounds(group.getBounds(), { padding: [40, 40] });
  } else if (mapMarkers.length === 1) {
    mapInstance.setView(mapMarkers[0].getLatLng(), 12);
  }
}

function highlightMarker(eventId: string): void {
  mapMarkers.forEach((m) => {
    if (m.eventId === eventId) {
      m.getElement()?.classList.add('highlighted');
    }
  });
}

function unhighlightMarkers(): void {
  mapMarkers.forEach((m) => {
    m.getElement()?.classList.remove('highlighted');
  });
}

function setupStatusFilters(): void {
  const tabs = document.getElementById('status-tabs');
  tabs?.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const button = target.closest('.status-tab') as HTMLButtonElement;
    if (!button) return;

    const status = button.dataset.status || '';

    // Update URL
    const url = status ? `/events?status=${status}` : '/events';
    window.history.pushState({}, '', url);

    // Update active state
    document.querySelectorAll('.status-tab').forEach((tab) => tab.classList.remove('active'));
    button.classList.add('active');

    loadEvents(status, true);
  });
}

function setupEventSearch(): void {
  const input = document.getElementById('event-search') as HTMLInputElement;
  let timeout: ReturnType<typeof setTimeout> | null = null;

  input?.addEventListener('input', () => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      paginationState.searchQuery = input.value.trim();
      renderEventsList();
    }, 300);
  });
}

function setupViewToggle(): void {
  const toggle = document.getElementById('view-toggle');
  const listPanel = document.getElementById('events-list-panel');
  const mapPanel = document.getElementById('events-map-panel');

  let showingMap = false;

  toggle?.addEventListener('click', () => {
    showingMap = !showingMap;
    listPanel?.classList.toggle('events-list-panel--hidden', showingMap);
    mapPanel?.classList.toggle('events-map-panel--visible', showingMap);
    toggle.innerHTML = showingMap
      ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg><span>List</span>'
      : '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg><span>Map</span>';

    // Invalidate map size when showing
    if (showingMap && mapInstance) {
      setTimeout(() => mapInstance.invalidateSize(), 100);
    }
  });
}

function setupInfiniteScroll(): void {
  let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

  window.addEventListener('scroll', () => {
    if (scrollTimeout) return;
    scrollTimeout = setTimeout(() => {
      scrollTimeout = null;
      const scrollPos = window.innerHeight + window.scrollY;
      const pageHeight = document.documentElement.scrollHeight;
      if (
        scrollPos >= pageHeight - 500 &&
        paginationState.hasMore &&
        !paginationState.isLoading
      ) {
        loadEvents(paginationState.statusFilter, false);
      }
    }, 100);
  });
}

export async function initEventsPage(
  container: HTMLElement,
  filters?: { status?: string }
): Promise<void> {
  // SEO
  setPageMeta({
    title: 'Events - Ecstatic Oracle',
    description: 'Discover TRNG events near you. Join ecstatic dance sessions, meditation circles, and consciousness exploration events.',
  });
  setCanonical('https://play.ecstaticoracle.dance/events');
  setJsonLd({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': 'Events - Ecstatic Oracle',
    'description': 'Discover TRNG events near you.',
    'url': 'https://play.ecstaticoracle.dance/events',
  });

  const breadcrumbs = renderBreadcrumbs([
    { label: 'Home', href: '/' },
    { label: 'Events' },
  ]);

  const statusFilters = [
    { id: '', label: 'All', icon: '&#x2728;' },
    { id: 'lobby', label: 'Upcoming', icon: '&#x1F551;' },
    { id: 'active', label: 'Live Now', icon: '&#x1F534;' },
    { id: 'ended', label: 'Past', icon: '&#x2705;' },
  ];

  const activeStatus = filters?.status || '';

  container.innerHTML = `
    ${breadcrumbs}
    <div class="events-page">
      <header class="events-header">
        <div>
          <h1 class="events-title">Events</h1>
          <p class="events-subtitle">Discover TRNG sessions and consciousness exploration events</p>
        </div>
      </header>

      <div class="events-filters">
        <div class="events-search-wrap">
          <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input type="text" id="event-search" class="search-input" placeholder="Search events by name or location..." />
        </div>
        <div class="events-status-tabs" id="status-tabs">
          ${statusFilters.map((f) => `
            <button class="status-tab ${activeStatus === f.id ? 'active' : ''}" data-status="${f.id}">
              <span>${f.icon}</span>
              <span class="status-tab-label">${f.label}</span>
            </button>
          `).join('')}
        </div>
      </div>

      <div class="events-split">
        <div class="events-list-panel" id="events-list-panel">
          <div class="events-list-header">
            <span class="events-count" id="events-count"></span>
            <button class="view-toggle" id="view-toggle" title="Toggle map view">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <span>Map</span>
            </button>
          </div>
          <div class="events-list" id="events-list">
            <div class="loading-state">
              <div class="loading-spinner"></div>
              <span>Loading events...</span>
            </div>
          </div>
        </div>

        <div class="events-map-panel" id="events-map-panel">
          <div id="events-map" class="events-map"></div>
        </div>
      </div>

      <div id="cta"></div>
      <div id="footer"></div>
    </div>
  `;

  setupStatusFilters();
  setupEventSearch();
  setupViewToggle();
  setupInfiniteScroll();

  const ctaEl = document.getElementById('cta');
  const footerEl = document.getElementById('footer');
  if (ctaEl) renderDownloadCTA(ctaEl);
  if (footerEl) renderFooter(footerEl);

  await loadEvents(activeStatus, true);
  await initEventsMap();
}
